import { isAfter, isBefore, isSameDay, subHours, addHours, parseISO } from 'date-fns';
import {
  FilterComparator,
  type ComparisonFilterExpression,
  type FilterExpression,
  FilterOperator,
  isComparisonFilterExpression,
  isGroupingFilterExpression,
} from '@shared/libs/search/types';

function validateFilterExpression(filter: unknown) {
  // must be object
  if (typeof filter !== 'object' || Array.isArray(filter) || !filter) return false;

  let nestsMultipleExpressions = false;
  if (
    'operator' in filter &&
    typeof filter.operator === 'string' &&
    'operands' in filter &&
    filter.operands != null
  ) {
    nestsMultipleExpressions = true;
    // only supported operations
    if (!['and', 'or'].includes(filter.operator)) return false;

    // must have list of operands of any length
    if (!Array.isArray(filter.operands)) return false;

    // every operand must be valid
    for (const o of filter.operands) if (!validateFilterExpression(o)) return false;
  }

  let comparesFieldToValue = false;
  if (
    'field' in filter &&
    filter.field != null &&
    'comparator' in filter &&
    filter.comparator != null &&
    'value' in filter &&
    filter.value !== undefined
  ) {
    comparesFieldToValue = true;
    // non-empty field name
    if (typeof filter.field !== 'string' || filter.field === '') return false;
    // non-empty comparator
    if (typeof filter.comparator !== 'string' || filter.comparator === '') return false;
  }

  if (
    (nestsMultipleExpressions && comparesFieldToValue) ||
    (!nestsMultipleExpressions && !comparesFieldToValue)
  ) {
    return false;
  }

  return true;
}

// validateSearchOptions returns true if formatted correctly based on the rules defined in:
// https://www.notion.so/riskledger/Standard-Search-Interface-bc0b46f109524af3bc96405441543340
// This should help any code verify that it's constructing the search options correctly for sending to the backend.
export const validateSearchOptions = (options: any) => {
  if (!options) return false;

  // pagination is optional
  if (options.page != null) {
    // must be object of pagination options
    if (typeof options.page !== 'object' || Array.isArray(options.page)) return false;

    // limit is optional int >= 0
    if (
      options.page.limit != null &&
      !(Number.isInteger(options.page.limit) && options.page.limit >= 0)
    ) {
      return false;
    }

    const offsetChosen = options.page.offset != null;
    const cursorChosen = options.page.cursor != null;
    if ((offsetChosen ? 1 : 0) + (cursorChosen ? 1 : 0) !== 1) return false; // either offset or cursor pagination must be chosen
    if (offsetChosen && !(Number.isInteger(options.page.offset) && options.page.offset >= 0)) {
      return false;
    }
    if (cursorChosen && typeof options.page.cursor !== 'string') return false; // cursor can be empty
  }

  // sorting is optional
  if (options.sort != null) {
    // must be array of sorting options
    if (!Array.isArray(options.sort)) return false;

    for (const sort of options.sort) {
      // field must be non-empty string
      if (typeof sort.field !== 'string' || sort.field === '') return false;
      // direction must be ascending or descending
      if (!['asc', 'desc'].includes(sort.direction)) return false;
    }
  }

  // filter is optional
  if (options.filter != null && !validateFilterExpression(options.filter)) return false;

  return true;
};

// Filters.vue attaches a uuid `id` to each operand for its own UI state -
// never part of the wire contract, and huma-validated endpoints reject it.
// Filters.vue and useSearchQueryParams both call this so `params.filter`
// never carries one.
export function stripFilterOperandIDs(
  filter: FilterExpression | undefined,
): FilterExpression | undefined {
  if (!filter || !isGroupingFilterExpression(filter)) return filter;
  return {
    ...filter,
    operands: filter.operands.map((operand) => {
      const { id: _id, ...rest } = operand;
      return stripFilterOperandIDs(rest as FilterExpression) as FilterExpression;
    }),
  };
}

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

const isNumberArray = (value: unknown): value is number[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'number');

function parseDurationHours(value: ComparisonFilterExpression['value']): number | null {
  if (typeof value !== 'string') return null;
  const match = value.match(/^(\d+)h$/);
  if (!match) return null;
  return parseInt(match[1] as string, 10);
}

function evaluateRelativeDateComparator(
  fieldValue: unknown,
  filterValue: ComparisonFilterExpression['value'],
  direction: 'past' | 'future',
  compare: (fieldDate: Date, cutoff: Date) => boolean,
): boolean {
  const hours = parseDurationHours(filterValue);
  if (hours === null || typeof fieldValue !== 'string') return false;
  const shiftFn = direction === 'past' ? subHours : addHours;
  return compare(parseISO(fieldValue), shiftFn(new Date(), hours));
}

// When there is no backend filtering items, this function will use the same logic to filter in the frontend from a FilterExpression.
// This function returns whether an item matches the filter expression
export const evaluateFilterExpression = (
  filter: FilterExpression | undefined,
  item: any,
): boolean => {
  // If filter is invalid, match everything (don't filter)
  if (!validateFilterExpression(filter) || !filter) {
    return true;
  }

  if (isComparisonFilterExpression(filter)) {
    const fieldValue = item[filter.field];

    // Here are implemented some but not all of the comparators.
    switch (filter.comparator) {
      case FilterComparator.Equal:
        return fieldValue === filter.value;
      case FilterComparator.NotEqual:
        return fieldValue !== filter.value;
      case FilterComparator.GreaterThan:
        return fieldValue > filter.value;
      case FilterComparator.LessThan:
        return fieldValue < filter.value;
      case FilterComparator.GreaterOrEqual:
        return fieldValue >= filter.value;
      case FilterComparator.LessOrEqual:
        return fieldValue <= filter.value;
      case FilterComparator.Contains:
        return (fieldValue ?? '')
          .toString()
          .toLowerCase()
          .includes((filter.value ?? '').toString().toLowerCase());
      case FilterComparator.DoesNotContain:
        return !(fieldValue ?? '')
          .toString()
          .toLowerCase()
          .includes((filter.value ?? '').toString().toLowerCase());
      case FilterComparator.IsAny:
        if (isStringArray(filter.value)) {
          return typeof fieldValue === 'string' && filter.value.includes(fieldValue);
        }
        if (isNumberArray(filter.value)) {
          return typeof fieldValue === 'number' && filter.value.includes(fieldValue);
        }
        return false;
      case FilterComparator.IsNotAny:
        if (isStringArray(filter.value)) {
          return typeof fieldValue === 'string' && !filter.value.includes(fieldValue);
        }
        if (isNumberArray(filter.value)) {
          return typeof fieldValue === 'number' && !filter.value.includes(fieldValue);
        }
        return false;
      case FilterComparator.IncludesAny:
        return (
          Array.isArray(filter.value) &&
          Array.isArray(fieldValue) &&
          filter.value.some((v) => fieldValue.includes(v))
        );
      case FilterComparator.IncludesAll:
        return (
          Array.isArray(filter.value) &&
          Array.isArray(fieldValue) &&
          filter.value.every((v) => fieldValue.includes(v))
        );
      case FilterComparator.ExcludesAny:
        return (
          Array.isArray(filter.value) &&
          Array.isArray(fieldValue) &&
          filter.value.some((v) => !fieldValue.includes(v))
        );
      case FilterComparator.ExcludesAll:
        return (
          Array.isArray(filter.value) &&
          Array.isArray(fieldValue) &&
          filter.value.every((v) => !fieldValue.includes(v))
        );
      case FilterComparator.EqualDate:
        return (
          typeof fieldValue === 'string' &&
          typeof filter.value === 'string' &&
          isSameDay(parseISO(fieldValue), parseISO(filter.value))
        );
      case FilterComparator.NotEqualDate:
        return (
          typeof fieldValue === 'string' &&
          typeof filter.value === 'string' &&
          !isSameDay(parseISO(fieldValue), parseISO(filter.value))
        );
      case FilterComparator.RelativePastGreaterThan:
        return evaluateRelativeDateComparator(fieldValue, filter.value, 'past', isAfter);
      case FilterComparator.RelativePastLessThan:
        return evaluateRelativeDateComparator(fieldValue, filter.value, 'past', isBefore);
      case FilterComparator.RelativePastEqualDate:
        return evaluateRelativeDateComparator(fieldValue, filter.value, 'past', isSameDay);
      case FilterComparator.RelativeFutureGreaterThan:
        return evaluateRelativeDateComparator(fieldValue, filter.value, 'future', isAfter);
      case FilterComparator.RelativeFutureLessThan:
        return evaluateRelativeDateComparator(fieldValue, filter.value, 'future', isBefore);
      case FilterComparator.RelativeFutureEqualDate:
        return evaluateRelativeDateComparator(fieldValue, filter.value, 'future', isSameDay);
      default:
        return false;
    }
  }

  if (isGroupingFilterExpression(filter)) {
    if (filter.operands.length === 0) {
      return true;
    }

    return (
      (filter.operands.every((operand) => evaluateFilterExpression(operand, item)) &&
        filter.operator === FilterOperator.And) ||
      (filter.operator === FilterOperator.Or &&
        filter.operands.some((operand) => evaluateFilterExpression(operand, item)))
    );
  }

  return false;
};

export const filterWithFilterExpression = <TItem>(
  filter: FilterExpression,
  items: TItem[],
): TItem[] => {
  if (items.length === 0) {
    return items;
  }

  return items.filter((item) => evaluateFilterExpression(filter, item));
};
